<template>
  <div class="capability-grid" :class="gridClass">
    <router-link
      v-for="item in items"
      :key="item.key"
      class="capability-link"
      :to="{ path: '/business-categories', query: { business: item.key } }"
    >
      <article class="capability-card" :class="item.variant">
        <div class="capability-figure">
          <img :src="capabilityImage(item)" :alt="item.title">
        </div>
        <div class="capability-copy">
          <h3>{{ item.title }}</h3>
          <p>{{ item.description }}</p>
          <div class="tags">
            <span v-for="tag in item.tags" :key="tag">{{ tag }}</span>
          </div>
        </div>
      </article>
    </router-link>
  </div>
</template>

<script>
export default {
  name: "CapabilityGrid",
  props: {
    items: {
      type: Array,
      default: () => []
    },
    showCode: {
      type: Boolean,
      default: true
    },
    columns: {
      type: Number,
      default: 4
    }
  },
  computed: {
    gridClass() {
      return this.columns === 3 ? "is-three-columns" : "";
    }
  },
  methods: {
    capabilityImage(item) {
      return (item && item.image) || "/assets/eut.png";
    }
  }
};
</script>

<style lang="less" scoped>
.capability-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  background: rgba(29, 104, 199, .18);
  border: 1px solid var(--line);
}

.capability-grid.is-three-columns {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.capability-link {
  display: block;
  height: 100%;
  min-width: 0;
  color: inherit;
}

.capability-card {
  min-height: 228px;
  height: 100%;
  display: flex;
  align-items: stretch;
  gap: 15px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.capability-link:hover .capability-card {
  transform: translateY(-4px);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 22px 40px rgba(8, 43, 84, 0.14);
}

.capability-card.blue {
  color: var(--white);
  background: linear-gradient(135deg, #1d68c7, #0b4c9a);
}

.capability-card.green {
  color: var(--white);
  background: linear-gradient(135deg, #1479c9, #075b9f);
}

.capability-card.amber {
  color: var(--white);
  background: linear-gradient(135deg, #4aa3ff, #1d68c7);
}

.capability-card.navy {
  color: var(--white);
  background: linear-gradient(135deg, #0b2b53, #073f78);
}

.capability-figure {
  flex: 0 0 45%;
  min-width: 220px;
  border-radius: 8px;
  overflow: hidden;
  background: #e8f2fb;
}

.capability-figure img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.capability-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.capability-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.code {
  font-size: 13px;
  font-weight: 950;
  opacity: 0.68;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.42;
}

.capability-card h3 {
  margin: 0 0 12px;
  font-size: 22px;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.capability-card p {
  margin: 0;
  color: inherit;
  opacity: 0.78;
  font-size: 15px;
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
}

.tags span {
  min-height: 27px;
  display: inline-flex;
  align-items: center;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid currentColor;
  border-radius: 999px;
  opacity: 0.72;
}

@media (max-width: 1260px) {
  .capability-grid,
  .capability-grid.is-three-columns {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1040px) {
  .capability-grid,
  .capability-grid.is-three-columns {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .capability-card {
    gap: 16px;
    padding: 18px;
  }

  .capability-figure {
    flex-basis: 38%;
    min-width: 104px;
  }
}

@media (max-width: 680px) {
  .capability-grid,
  .capability-grid.is-three-columns {
    grid-template-columns: 1fr;
    gap: 12px;
    border: 0;
    background: transparent;
  }

  .capability-card {
    min-height: 0;
    padding: 14px;
    gap: 14px;
    border: 1px solid var(--line);
    border-radius: 8px;
  }

  .capability-card h3 {
    margin: 0 0 10px;
    font-size: 20px;
  }

  .capability-card p {
    font-size: 14px;
    line-height: 1.6;
  }

  .tags {
    gap: 6px;
    margin-top: 14px;
  }

  .tags span {
    min-height: 24px;
    padding: 0 8px;
    font-size: 11px;
  }
}

@media (max-width: 520px) {
  .capability-card {
    flex-direction: column;
  }

  .capability-figure {
    flex: none;
    width: 100%;
    min-width: 0;
    aspect-ratio: 16 / 9;
  }
}
</style>
